// keywordAutocomplete/attachKeywordAutocomplete.js — the controller that turns a contenteditable element into a
// keyword autocomplete, using the caret I/O adapter (caretWord), an injected suggestion source, and the dropdown.
//
//   input  → getWordAtCaret → (debounce) → source(term) → menu.show(items, caretRect)
//   keydown→ ↑/↓/Enter/Esc forwarded to the menu (the editor keeps focus)
//   select → spliceTermAtCaret → setValue(el, text) → setCaretCharOffset → onChange
//
// Reusable across fields: pass the field-specific `source` (e.g. createQuicklistSource) and, for a highlight
// editor, a `setValue` that re-renders the marks. Returns { destroy } (createDisposers teardown).

import { createDisposers } from "../core/disposers.js";
import { getWordAtCaret, caretCharOffset, spliceTermAtCaret, setCaretCharOffset } from "./caretWord.js";
import { createSuggestionMenu } from "./suggestionMenu.js";

export function attachKeywordAutocomplete(el, {
    source,
    minLength = 3,
    delay = 300,
    addSeparator = true,
    setValue = (node, text) => { node.textContent = text; },
    onChange = () => {},
} = {}) {
    const disposers = createDisposers();

    const menu = createSuggestionMenu({
        onSelect: (item) => {
            const offset = caretCharOffset(el);
            if (offset === null) { return; }
            const { text, caret } = spliceTermAtCaret(el.textContent, offset, item.label, { addSeparator });
            setValue(el, text);
            setCaretCharOffset(el, caret);
            onChange(text);
        },
    });
    disposers.add(() => menu.destroy());

    let timer = null;
    let seq = 0;

    // Ankerpunkt der Vorschlagsliste: WAAGRECHT die linke Kante des Feldes, SENKRECHT die aktuelle Zeile.
    // Vorher kam beides vom Cursor — die Liste wanderte beim Tippen mit und sprang bei jedem Buchstaben ein
    // Stück nach rechts (User 2026-09-25). Jetzt steht ihr linker Rand fest unter dem ersten Zeichen des
    // Feldes, während sie bei mehrzeiliger Eingabe weiterhin unter der Zeile aufgeht, in der geschrieben wird.
    //
    // BEIDE Kanten der Zeile werden geliefert, nicht nur die untere: die Liste klappt nach oben um, wenn unten
    // kein Platz mehr ist (suggestionMenu.placeAt), und setzt dafür ihre Unterkante an `top`. Bezugsgröße ist
    // die Zeile, nicht das Feld — sonst stünde die umgeklappte Liste bei mehrzeiliger Eingabe über dem ganzen
    // Feld statt über der Zeile, in der geschrieben wird. Das `|| field`-Rückfallpaar bleibt für den Fall, dass
    // das Cursor-Rechteck entartet ist (Breite und Höhe 0, kommt bei leerer Auswahl vor).
    function anchorRect() {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) { return null; }
        try {
            const caret = sel.getRangeAt(0).getBoundingClientRect();
            const field = el.getBoundingClientRect();
            return { left: field.left, top: caret.top || field.top, bottom: caret.bottom || field.bottom };
        } catch (e) { return null; }
    }

    // Ist das Feld überhaupt noch da? Zwei Stufen, beide nötig:
    //   `isConnected`      — das Element wurde aus dem Dokument entfernt
    //   `checkVisibility()` — es selbst ODER ein Vorfahr steht auf display:none/visibility:hidden; genau der
    //                        Fall, wenn der Tag-Rahmen ausgeblendet wird, weil das Video läuft und der Tag aus
    //                        seinem Zeitfenster fällt.
    // Bewusst NICHT über `offsetParent === null`: das wäre kürzer, verlässt sich aber auf Layout — in einer
    // Testumgebung ohne Layout (jsdom) ist es IMMER null und das Feld gälte dort als verschwunden.
    // Wo checkVisibility fehlt, bleibt es bei der Verbindungsprüfung: lieber eine Liste zu viel offen als eine
    // Vorschlagsliste, die nie erscheint.
    const fieldGone = () => !el.isConnected ||
        (typeof el.checkVisibility === 'function' ? !el.checkVisibility() : false);

    function query() {
        const term = getWordAtCaret(el);
        if (!term || term.length < minLength) { menu.hide(); return; }
        const mySeq = ++seq;
        Promise.resolve(source(term)).then((items) => {
            if (mySeq !== seq) { return; }   // a newer query started → drop this stale response
            // Das Feld kann zwischen Abfrage und Antwort verschwunden sein — die Abfrage ist entprellt, die
            // Quelle asynchron. Ohne diese Prüfung öffnete sich die Liste NACH dem Ausblenden des Tags wieder
            // und blieb sichtbar hängen, ohne Feld, zu dem sie gehört (User 2026-09-25). Die Prüfung auf
            // veraltete Antworten oben fängt das nicht: die Antwort ist die aktuellste, nur ihr Ziel ist weg.
            if (fieldGone()) { menu.hide(); return; }
            menu.show(items, anchorRect(), el);
        }).catch(() => { /* source error → no menu */ });
    }

    const on = (node, ev, fn) => { node.addEventListener(ev, fn); disposers.add(() => node.removeEventListener(ev, fn)); };

    on(el, "input", () => { clearTimeout(timer); timer = setTimeout(query, delay); });
    on(el, "keydown", (ev) => {
        if (!menu.isOpen()) { return; }
        if (ev.key === "ArrowDown") { ev.preventDefault(); menu.highlightNext(); }
        else if (ev.key === "ArrowUp") { ev.preventDefault(); menu.highlightPrev(); }
        else if (ev.key === "Enter") { ev.preventDefault(); menu.selectHighlighted(); }
        else if (ev.key === "Escape") { ev.preventDefault(); menu.hide(); }
    });
    // hide shortly after blur (mousedown-select already fired by then)
    on(el, "blur", () => { setTimeout(() => menu.hide(), 100); });

    // Zweite Leine für den Fall, dass gar kein blur kommt: verschwindet der Tag-Rahmen, während die Liste offen
    // ist, bemerkt das sonst niemand. Läuft nur, solange die Liste offen ist, und hängt sich an dieselbe
    // Aufräumkette wie alles andere.
    const visibilityTimer = setInterval(() => {
        if (menu.isOpen() && fieldGone()) { menu.hide(); }
    }, 500);
    disposers.add(() => clearInterval(visibilityTimer));

    return { destroy: () => { clearTimeout(timer); disposers.dispose(); } };
}
